import { HTMLProps, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { DebouncedInputSearch } from '@components/debounced-input-search';
import { IconChevron } from '@components/icons';
import { Input } from '@components/input';
import { InputCheckbox } from '@components/input-checkbox';
import { Loader } from '@components/loader';
import { Channel } from '@domain/channel';
import { useOnClickOutside } from '@hooks/use-click-outside';
import classNames from 'classnames';

export interface Props extends Omit<HTMLProps<HTMLInputElement>, 'onChange' | 'value'> {
  options: Channel[];
  handleSelectedAll: (value: boolean) => void;
  onChange: (id: string, value: boolean) => void;
  translations: {
    deselectAll: string;
    selectAll: string;
    noResultsFound: string;
    noOptionsSelected: string;
    searchLabel: string;
    searchPlaceholder: string;
  };
  loadingText?: string;
  children?: JSX.Element | ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  isCheckboxRounded?: boolean;
  parentRef?: React.RefObject<HTMLDivElement | HTMLFormElement>;
}

// eslint-disable-next-line max-lines-per-function
export const MultiselectSearch = ({
  id,
  hasError,
  options = [],
  onChange,
  isLoading = false,
  children,
  handleSelectedAll,
  isCheckboxRounded = false,
  translations,
  parentRef,
  readOnly,
  ...props
}: Props) => {
  const itemsEls = useRef<HTMLLIElement[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownList = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [cursor, setCursor] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOnClickOutside(wrapperRef, handleClickOutside);

  const setInputFocused = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const calculateDropdownPosition = useCallback(() => {
    const dropdownListComponent = dropdownList.current;
    if (!dropdownListComponent || !inputRef.current || !parentRef?.current) return;
    const availableSpaceBelow =
      parentRef.current.clientHeight - inputRef.current.getBoundingClientRect().bottom <
      dropdownListComponent.clientHeight;
    dropdownListComponent.style.cssText = availableSpaceBelow ? 'bottom: 100%; top: auto' : 'top: 100%; bottom: auto';
  }, [parentRef]);

  const handleClickInside = useCallback(() => {
    setIsOpen(true);
    setCursor(0);
    setInputFocused();
  }, [setInputFocused]);

  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen, calculateDropdownPosition]);

  const moveToCursor = useCallback(() => {
    if (itemsEls.current[cursor]) {
      itemsEls.current[cursor].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'end',
      });
    }
  }, [cursor]);

  useEffect(() => {
    setCursor(0);
  }, [options]);

  useEffect(() => {
    if (isOpen) {
      moveToCursor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleOnChangeTerm = (value: string) => {
    setSearchTerm(value.toLowerCase());
  };

  const handleChange = () => {
    // TODO: handle search
  };

  const handleClickSelectAll = () => {
    const areAllChecked = !options.every((option) => option.checked);
    handleSelectedAll(areAllChecked);
    setIsOpen(areAllChecked);
  };

  const channelsSorted = [...options].sort((a: Channel, b: Channel): number =>
    a.name > b.name ? 1 : b.name > a.name ? -1 : 0
  );
  const selectedOptions = channelsSorted.filter((option) => !!option.checked);
  const readableValue = selectedOptions.length
    ? `(${selectedOptions.length}) ${selectedOptions
        .slice(0, 10)
        .map((selectedOption) => selectedOption.name)
        .join(', ')}`
    : translations.noOptionsSelected;
  const areAllSelected = channelsSorted.every((option) => option.checked);
  const areAllSelectedLabel = areAllSelected ? translations.deselectAll : translations.selectAll;
  const filteredOptions = channelsSorted.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // TOO: i18n
  return (
    <div className='mt-1 relative' ref={wrapperRef}>
      <div
        className={`flex gap-x-2 gap-y-1 flex-wrap items-center bg-primary relative w-full rounded-md border text-body-s text-primary placeholder-gray-500 focus-within:outline-none focus-within:ring-0 lg:text-body-m ${classNames(
          {
            'border-critical-no-bg': hasError,
            'border-soft': !hasError,
            'focus-within:border-hard': !hasError,
          }
        )}`}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-labelledby={`${id}-button ${id}`}
        id={`${id}-button`}>
        <div className='relative w-full'>
          <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
            <IconChevron className='-rotate-90 w-3 h-3 text-gray-900' aria-hidden={true} />
          </div>
          <Input
            {...props}
            id={id}
            onClick={readOnly ? undefined : handleClickInside}
            value={readableValue}
            onChange={readOnly ? undefined : handleChange}
            className='truncate'
            type='w-full text flex flex-grow'
            ref={inputRef}
            readOnly
            isChangingInactiveColor={false}
          />
        </div>
      </div>

      <div
        className={classNames(
          'absolute z-30 mt-1 bg-primary shadow-sm rounded-md py-5 px-3 flex flex-col items-start dark:shadow-black-1000/30',
          {
            hidden: !isOpen || (!options.length && !selectedOptions.length),
            'items-center': isLoading,
          }
        )}
        ref={dropdownList}>
        <div className='relative w-full'>
          <label htmlFor='search' className='sr-only'>
            {translations.searchLabel}
          </label>
          <DebouncedInputSearch
            placeholder={translations.searchPlaceholder}
            onChange={handleOnChangeTerm}
            defaultValue={searchTerm}
            canDeleteSearch
            minCharacters={1}
            debounceTime={0}
          />
        </div>
        {isLoading ? (
          <div className='w-20 h-20 flex items-center'>
            <Loader />
          </div>
        ) : (
          <ul
            id={`${id}-list`}
            tabIndex={-1}
            role='listbox'
            aria-label={id}
            aria-labelledby={id}
            aria-activedescendant={id}
            className='mt-5 px-2 flex flex-col space-y-6 max-h-56 overflow-auto scrollbar'>
            {!options.length && (
              <li className='text-primary select-none relative py-2 px-3 cursor-pointer'>
                <span>{translations.noResultsFound}</span>
              </li>
            )}

            {!!options.length && (
              <>
                <InputCheckbox
                  name={`selectAll`}
                  label={areAllSelectedLabel}
                  value={areAllSelected}
                  rounded={isCheckboxRounded}
                  onChange={handleClickSelectAll}
                />
                {filteredOptions.map((option) => (
                  <InputCheckbox
                    key={option.id}
                    name={option.id}
                    label={option.name}
                    secondaryLabel={`(${option.slug})`}
                    value={option.checked}
                    rounded={isCheckboxRounded}
                    onChange={(val) => {
                      onChange(option.id, val);
                    }}
                  />
                ))}
              </>
            )}

            {children ? children : null}
          </ul>
        )}
      </div>
    </div>
  );
};
